import Header from "@/components/header";
// Import Features dynamically with SSR disabled to avoid framer-motion issues
const Features = dynamic(() => import("@/components/features"), {
  ssr: false,
  loading: () => <div className="h-96 bg-gray-100 animate-pulse rounded"></div>,
});
import Footer from "@/components/footer";
import VehicleSearchDemo from "@/components/vehicle-search-demo";
import dynamic from "next/dynamic";

// Import CallToAction dynamically with SSR disabled to avoid framer-motion issues
const CallToAction = dynamic(() => import("@/components/call-to-action"), {
  ssr: false,
  loading: () => <div className="h-32 bg-gray-100 animate-pulse rounded"></div>,
});
import { NextSeo } from "next-seo";

import Link from "next/link";
import {
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  TruckIcon,
  DatabaseIcon,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { titleInitial, titleAnimate } from "@/components/animations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import MapBackground from "@/components/map-background";
import Image from "next/image";

// Force SSR to avoid framer-motion static generation issues
export async function getServerSideProps() {
  return { props: {} };
}

export default function Home() {
  return (
    <div>
      <NextSeo
        title="Partbot - Automotive Product Sales Platform"
        description="Sync automotive parts and accessories to your ecommerce store,
        sell and ship direct to your customers. Save yourself
        endless hours of product management and order fulfilment."
      />

      <Header />
      <main>
        <MapBackground />
        <div className="relative flex min-h-[90dvh] flex-col items-end justify-end pt-24">
          <div className="container mx-auto">
            <div className="relative sm:overflow-hidden">
              <div className="mt:py-16 wrap-balance relative px-6 py-6 xl:px-6">
                <motion.h1
                  initial={titleInitial}
                  whileInView={titleAnimate()}
                  viewport={{ once: true }}
                  className="text-balance bg-linear-to-r pb-5 text-4xl font-bold leading-none drop-shadow-lg text-stone-50 lg:text-6xl xl:text-7xl lg:max-w-6xl"
                >
                  The Automotive Product Sales Platform
                </motion.h1>
                <motion.p
                  initial={titleInitial}
                  whileInView={titleAnimate(0.25)}
                  viewport={{ once: true }}
                  className="mt-3 max-w-prose text-balance text-lg text-emerald-300/75"
                >
                  Sync automotive parts and accessories to your ecommerce store,
                  power it with vehicle and rego search, automate supplier
                  fulfilment, and accelerate sales.
                </motion.p>
                <div className="mt-9">
                  <Button asChild>
                    <Link href="/contact">
                      <span className="mr-2">Contact Us</span>
                      <ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Logo Cloud */}
        <motion.div
          initial={titleInitial}
          whileInView={titleAnimate()}
          viewport={{ once: true }}
        >
          <div className="container mx-auto">
            <div className="px-6 py-16 pt-8 lg:px-6">
              <p className="text-balance text-sm font-semibold uppercase tracking-wide text-emerald-500/50 md:text-center">
                Sell automotive parts across ecommerce platforms and
                marketplaces
              </p>
              <div className="mt-6 grid grid-cols-3 place-content-center justify-items-center gap-12 md:mt-12 md:grid-cols-6 md:gap-8 lg:grid-cols-5">
                <div className="col-span-1 flex items-center md:col-span-2 md:col-start-4 md:justify-center lg:col-span-1">
                  <img
                    className="h-4 opacity-75 brightness-200"
                    src="./maropost-logo.svg"
                    alt="Maropost (Neto)"
                  />
                </div>
                <div className="col-span-1 flex items-center md:order-0 md:col-span-2 md:justify-center lg:col-span-1">
                  <img
                    className="h-8 opacity-75 brightness-200 md:-mt-1"
                    src="./woocommerce-logo.svg"
                    alt="WooCommerce"
                  />
                </div>
                <div className="order-first col-span-1 flex items-center justify-center md:order-0 md:col-span-2 lg:col-span-1">
                  <img
                    className="h-8 opacity-75 brightness-200 md:-mt-3"
                    src="./shopify-logo.svg"
                    alt="Shopify"
                  />
                </div>
                <div className="col-span-1 flex items-center justify-center md:col-span-2 lg:col-span-1">
                  <img
                    className="h-5 opacity-75 brightness-200 md:-mt-1 md:h-8"
                    src="./amazon-logo.svg"
                    alt="Amazon"
                  />
                </div>
                <div className="col-span-1 -mt-1 flex items-center justify-center md:col-span-2 md:col-start-2 lg:col-span-1">
                  <img
                    className="h-5 opacity-75 brightness-200 md:h-8"
                    src="./ebay-logo.svg"
                    alt="Ebay"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Alternating Feature Sections */}
        <div className="relative grid gap-20 overflow-hidden pb-12 pt-16">
          <div className="relative">
            <div className="container lg:mx-auto lg:grid lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24">
              <div className="mx-auto max-w-3xl px-6 lg:mx-0 lg:py-16">
                <div>
                  <motion.div
                    initial={titleInitial}
                    whileInView={titleAnimate(0.25)}
                    viewport={{ once: true }}
                    className="flex items-center gap-2"
                  >
                    <DatabaseIcon
                      className="fa-swap-opacity h-4 w-4 text-amber-500"
                      aria-hidden="true"
                    />

                    <Badge variant="secondary">Catalogue Data</Badge>
                  </motion.div>
                  <div className="mt-6">
                    <motion.h2
                      initial={titleInitial}
                      whileInView={titleAnimate(0.5, 1)}
                      viewport={{ once: true }}
                      className="text-balance bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text text-3xl font-bold text-transparent md:text-4xl"
                    >
                      Source Automotive Parts & Accessories
                    </motion.h2>
                    <motion.div
                      initial={titleInitial}
                      whileInView={titleAnimate(0.75, 1)}
                      viewport={{ once: true }}
                      className="prose mt-4 text-balance text-emerald-100/50 md:mt-8"
                    >
                      <p>
                        Search the Partbot catalogue for the automotive parts
                        you want to sell. Access new products from aftermarket
                        brands. Instantly import to your store and start
                        selling.
                      </p>
                    </motion.div>
                    <Button size="xs" className="mt-8 bg-emerald-500" asChild>
                      <Link href="/contact">
                        <span>Get started</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-12 lg:mt-0">
                <motion.div
                  initial={{
                    x: 100,
                    opacity: 0,
                  }}
                  whileInView={{
                    x: 0,
                    opacity: 1,
                    transition: {
                      duration: 2.5,
                      delay: 0.5,
                      ease: "easeOut",
                    },
                  }}
                  viewport={{ once: true }}
                  className="-mr-48 md:-mr-16 lg:relative lg:m-0 lg:h-full lg:px-0"
                >
                  <div className="relative aspect-video w-full overflow-clip rounded-3xl bg-emerald-500 shadow-xl ring-1 ring-black ring-opacity-5 lg:left-0 lg:h-[500px] lg:w-auto lg:max-w-none">
                    <Image
                      src="/images/partbot-product-sourcing.png"
                      fill={true}
                      quality={100}
                      alt="Source automotive parts and import to your ecommerce store"
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
          <div className="">
            <div className="container lg:mx-auto lg:grid lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24">
              <div className="mx-auto max-w-3xl px-6 md:pr-16 lg:mx-0 lg:py-16">
                <div>
                  <motion.div
                    initial={titleInitial}
                    whileInView={titleAnimate(0.25)}
                    viewport={{ once: true }}
                    className="flex items-center gap-2"
                  >
                    <ArrowsClockwiseIcon
                      className="h-4 w-4 text-amber-500"
                      aria-hidden="true"
                    />
                    <Badge variant="secondary">Real-time Sync</Badge>
                  </motion.div>
                  <div className="mt-6">
                    <motion.h2
                      initial={titleInitial}
                      whileInView={titleAnimate(0.5, 1)}
                      viewport={{ once: true }}
                      className="text-balance bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text text-3xl font-bold text-transparent md:text-4xl"
                    >
                      Inventory, Pricing and Product Updates on Autopilot.
                    </motion.h2>
                    <motion.div
                      initial={titleInitial}
                      whileInView={titleAnimate(0.75, 1)}
                      viewport={{ once: true }}
                      className="prose mt-4 text-balance text-emerald-100/50 md:mt-8"
                    >
                      <p>
                        Never worry about supplier inventory levels, price
                        updates or product data changes ever again. Keep your
                        listings in sync with brands and suppliers.
                      </p>
                    </motion.div>
                    <Button size="xs" className="mt-8 bg-emerald-500" asChild>
                      <Link href="/contact">
                        <span>Get started</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="-ml-[33vw] mt-12 lg:relative lg:col-start-1 lg:mt-0">
                <motion.div
                  initial={{
                    x: -100,
                    opacity: 0,
                  }}
                  whileInView={{
                    x: 0,
                    opacity: 1,
                    transition: {
                      duration: 2.5,
                      delay: 0.5,
                      ease: "easeOut",
                    },
                  }}
                  viewport={{ once: true }}
                  className="flex justify-end lg:m-0 lg:h-full lg:px-0"
                >
                  <div className="relative aspect-video w-full overflow-clip rounded-3xl bg-emerald-500 shadow-xl ring-1 ring-black ring-opacity-5 lg:left-0 lg:h-[500px] lg:w-auto lg:max-w-none">
                    <Image
                      src="/images/partbot-data-sync.png"
                      fill={true}
                      quality={100}
                      alt="Sync inventory, pricing and product changes to your online shop"
                      className="h-auto w-full object-cover object-right"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
          {/* Fulfilment */}
          <div className="">
            <div className="container pb-20 lg:mx-auto lg:grid lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-6">
              <div className="mx-auto max-w-3xl px-6 lg:mx-0 lg:py-16">
                <div>
                  <motion.div
                    initial={titleInitial}
                    whileInView={titleAnimate(0.25)}
                    viewport={{ once: true }}
                    className="flex items-center gap-2"
                  >
                    <TruckIcon
                      className="h-4 w-4 text-amber-500"
                      aria-hidden="true"
                    />

                    <Badge variant="secondary">Fulfilment</Badge>
                  </motion.div>
                  <div className="mt-6">
                    <motion.h2
                      initial={titleInitial}
                      whileInView={titleAnimate(0.5, 1)}
                      viewport={{ once: true }}
                      className="text-balance bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text text-3xl font-bold text-transparent md:text-4xl"
                    >
                      Simple, Automated Wholesale Fulfilment
                    </motion.h2>
                    <motion.div
                      initial={titleInitial}
                      whileInView={titleAnimate(0.75, 1)}
                      viewport={{ once: true }}
                      className="prose mt-4 text-balance text-emerald-100/50 md:mt-8"
                    >
                      <p>
                        Automate your order fulfilments with Partbot&apos;s
                        smart distributor routing. Orders are automatically
                        routed to your distributors based on stock availability,
                        location and shipping costs.
                      </p>
                    </motion.div>
                    <Button size="xs" className="mt-8 bg-emerald-500" asChild>
                      <Link href="/contact">
                        <span>Get started</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-12 lg:mt-0">
                <motion.div
                  initial={{
                    x: 100,
                    opacity: 0,
                  }}
                  whileInView={{
                    x: 0,
                    opacity: 1,
                    transition: {
                      duration: 2.5,
                      delay: 0.5,
                      ease: "easeOut",
                    },
                  }}
                  viewport={{ once: true }}
                  className="-mr-48 md:-mr-16 lg:relative lg:m-0 lg:h-full lg:px-0"
                >
                  <div className="relative aspect-video w-full overflow-clip rounded-3xl bg-emerald-500 shadow-xl ring-1 ring-black ring-opacity-5 lg:left-0 lg:h-[500px] lg:w-auto lg:max-w-none">
                    <Image
                      src="/images/partbot-dropship-fulfilment.png"
                      fill={true}
                      quality={100}
                      alt="Source automotive parts and import to your ecommerce store"
                      className="h-auto w-full object-cover object-left"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        <Features />
        <VehicleSearchDemo />

        {/* Brands section */}
        {/* <Brands /> */}

        {/* CTA Section */}
        <CallToAction />
      </main>

      <Footer />
    </div>
  );
}
